import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowsClockwise,
  ArrowSquareOut,
  EnvelopeSimple,
  List,
  PencilSimple,
  Plus,
  Sparkle,
  Trash,
} from '@phosphor-icons/react';

import {
  deleteBroadcast,
  listBroadcasts,
  type Broadcast,
} from '../api/broadcasts';
import { listWebsites, publicURL } from '../api/websites';
import { config } from '../config';
import { BroadcastAIDialog, type GeneratedDraft } from '../broadcasts/BroadcastAIDialog';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { useConfirm } from '../components/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  RowActions,
  StickyEndCell,
  useHorizontalScrollEdge,
  type RowAction,
} from '@/components/ui/row-actions';
import {
  SortableHeaderButton,
  useTableSortState,
} from '@/components/ui/table-sort';
import { cn } from '@/lib/utils';

// Broadcasts is the overview page at /orgs/:slug/broadcasts. Lists every
// non-deleted broadcast (drafts + sent + imported-from-seed) with row
// actions for edit, delete, and "open public" (for sent + website-
// published rows). The "New utskick" CTA opens BroadcastEditor at
// /orgs/:slug/broadcasts/new.

type SortColumn = 'subject' | 'status' | 'sent_at' | 'recipients';

export function BroadcastsPage() {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { slug = '' } = useParams<{ slug: string }>();
  const confirm = useConfirm();
  const token = auth.user?.access_token ?? null;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [rows, setRows] = useState<Broadcast[]>([]);
  const [aiOpen, setAiOpen] = useState(false);

  // A generated draft is carried into the editor via navigation state, so
  // nothing is persisted until the admin saves or sends — matching the
  // plain "New utskick" flow.
  const onGenerated = useCallback(
    (draft: GeneratedDraft) => {
      setAiOpen(false);
      navigate(`/orgs/${slug}/broadcasts/new`, { state: { aiDraft: draft } });
    },
    [navigate, slug],
  );

  // `background` skips the full-screen overlay so the table stays visible
  // while re-fetching (used by the refresh button, focus, and post-send
  // re-polls). The initial mount load uses the overlay.
  const load = useCallback(
    async (background = false) => {
      if (background) setRefreshing(true);
      else setLoading(true);
      try {
        setRows(await listBroadcasts(token, slug));
      } finally {
        if (background) setRefreshing(false);
        else setLoading(false);
      }
    },
    [slug, token],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  // Public-site base for the "View on site" row action. The published news
  // article lives on the PUBLIC website origin — a club subdomain in
  // production, the API origin's /c/<slug> in dev — never on the SPA origin,
  // and it's keyed by the WEBSITE slug, which an admin can rename away from the
  // org slug. A relative href got both wrong (it resolved against the SPA and
  // assumed org slug == website slug), so we resolve the live site once here
  // and hide the action entirely when the org has no published website.
  const [siteBase, setSiteBase] = useState('');
  useEffect(() => {
    if (!token || !slug) return;
    let cancelled = false;
    void listWebsites(token, slug)
      .then((sites) => {
        const live = sites.find((s) => s.status === 'published');
        if (!cancelled) {
          setSiteBase(
            live ? publicURL(config.apiURL, config.publicSitesBaseDomain, live.slug) : '',
          );
        }
      })
      .catch(() => {
        // No website module / no permission / offline — the action just stays
        // hidden. Never block the broadcast list on this.
        if (!cancelled) setSiteBase('');
      });
    return () => {
      cancelled = true;
    };
  }, [token, slug]);

  // Re-fetch when the tab regains focus so a broadcast that finished
  // sending in the background (Send is async server-side) shows its
  // updated status without a manual refresh.
  useEffect(() => {
    const onFocus = () => void load(true);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  // Arriving here right after pressing "Send" in the editor: the status
  // flips draft → sent only once the async fan-out completes, so re-poll a
  // few times to surface the change without the user having to refresh.
  const sentHandled = useRef(false);
  useEffect(() => {
    const sent = (location.state as { sent?: boolean } | null)?.sent;
    if (!sent || sentHandled.current) return;
    sentHandled.current = true;
    const timers = [1500, 4000, 8000].map((ms) =>
      window.setTimeout(() => void load(true), ms),
    );
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [location.state, load]);

  const handleDelete = useCallback(
    async (id: string, subject: string) => {
      if (
        !(await confirm({
          title: t('broadcast.list.deleteConfirmTitle'),
          body: t('broadcast.list.deleteConfirmBody', { subject }),
          confirmLabel: t('broadcast.list.delete'),
          tone: 'destructive',
        }))
      )
        return;
      await deleteBroadcast(token, slug, id);
      await load(true);
    },
    [confirm, load, slug, t, token],
  );

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 p-4 md:p-8">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('broadcast.list.eyebrow', { defaultValue: 'Utskick' })}
          </p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
            {t('broadcast.list.title', { defaultValue: 'Utskick' })}
          </h1>
        </div>
        {/* Below sm: consolidate header actions into a hamburger menu so the
            row never wraps under the title. From sm: up, the same actions
            render inline with full text labels (refresh included, since at
            that width an unlabelled icon reads as decorative). */}
        <div className="sm:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label={t('broadcast.list.actionsMenu', { defaultValue: 'Actions' })}
              >
                <List className="h-5 w-5" weight="bold" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[220px]">
              <DropdownMenuItem
                onSelect={() => navigate(`/orgs/${slug}/broadcasts/new`)}
              >
                <Plus className="h-4 w-4" weight="bold" />
                {t('broadcast.list.new', { defaultValue: 'New utskick' })}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setAiOpen(true)}>
                <Sparkle className="h-4 w-4" weight="fill" />
                {t('broadcast.ai.cta', { defaultValue: 'Create with AI' })}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={refreshing}
                onSelect={() => void load(true)}
              >
                <ArrowsClockwise
                  className={cn('h-4 w-4', refreshing && 'animate-spin')}
                  weight="bold"
                />
                {t('broadcast.list.refresh', { defaultValue: 'Refresh' })}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="hidden flex-wrap items-center gap-2 sm:flex">
          <Button
            type="button"
            variant="outline"
            onClick={() => void load(true)}
            disabled={refreshing}
          >
            <ArrowsClockwise
              className={cn('h-4 w-4', refreshing && 'animate-spin')}
              weight="bold"
            />
            {t('broadcast.list.refresh', { defaultValue: 'Refresh' })}
          </Button>
          <Button type="button" variant="outline" onClick={() => setAiOpen(true)}>
            <Sparkle className="h-4 w-4" weight="fill" />
            {t('broadcast.ai.cta', { defaultValue: 'Create with AI' })}
          </Button>
          <Button
            type="button"
            onClick={() => navigate(`/orgs/${slug}/broadcasts/new`)}
          >
            <Plus className="h-4 w-4" weight="bold" />
            {t('broadcast.list.new', { defaultValue: 'New utskick' })}
          </Button>
        </div>
      </header>

      {loading ? (
        <TableSkeleton columns={5} />
      ) : rows.length === 0 ? (
        <BroadcastsEmpty
          onAdd={() => navigate(`/orgs/${slug}/broadcasts/new`)}
          onAskAI={() => setAiOpen(true)}
        />
      ) : (
        <BroadcastList
          rows={rows}
          siteBase={siteBase}
          onEdit={(id) => navigate(`/orgs/${slug}/broadcasts/${id}`)}
          onDelete={handleDelete}
        />
      )}

      <BroadcastAIDialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        slug={slug}
        token={token}
        locale={i18n.language?.split('-')[0] ?? 'en'}
        onGenerated={onGenerated}
      />
    </div>
  );
}

function BroadcastsEmpty({ onAdd, onAskAI }: { onAdd: () => void; onAskAI: () => void }) {
  const { t } = useTranslation('common');
  return (
    <EmptyState
      icon={<EnvelopeSimple weight="duotone" />}
      title={t('broadcast.list.emptyTitle', { defaultValue: 'No broadcasts yet' })}
      body={t('broadcast.list.emptyBody', {
        defaultValue:
          "Click 'New utskick' to compose your first email to members. You can also publish utskick as news on your public website.",
      })}
      actions={[
        {
          label: t('broadcast.ai.cta', { defaultValue: 'Create with AI' }),
          onClick: onAskAI,
          icon: <Sparkle weight="fill" />,
        },
        {
          label: t('broadcast.list.new', { defaultValue: 'New utskick' }),
          onClick: onAdd,
          icon: <Plus weight="bold" />,
          variant: 'outline',
        },
      ]}
    />
  );
}

function BroadcastList({
  rows,
  siteBase,
  onEdit,
  onDelete,
}: {
  rows: Broadcast[];
  // Absolute origin of the org's published public website, or '' when it has
  // none — see the resolver in BroadcastsPage.
  siteBase: string;
  onEdit: (id: string) => void;
  onDelete: (id: string, subject: string) => void;
}) {
  const { t, i18n } = useTranslation('common');
  const scrollRef = useRef<HTMLDivElement>(null);
  const elevated = useHorizontalScrollEdge(scrollRef);
  // minmax() data columns (not bare fr) so the table never squeezes its
  // cells into an unreadable sliver — below the combined min width it
  // triggers horizontal scroll instead, which is what powers the
  // sticky-right actions elevation. Matches the Members / Teams rail.
  const template =
    'minmax(200px, 2.4fr) minmax(120px, 1fr) minmax(120px, 1.2fr) minmax(110px, 1fr) 4rem';

  // Locale-aware medium date ("28 May 2026"), built once and shared by
  // every row. Never the locale-default toLocaleDateString(), which renders
  // mm/dd/yyyy for en-US visitors (see CLAUDE.md date rule).
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language || undefined, { dateStyle: 'medium' }),
    [i18n.language],
  );

  const sortState = useTableSortState<SortColumn>({
    columns: ['subject', 'status', 'sent_at', 'recipients'] as const,
  });

  const sorted = useMemo(() => {
    if (!sortState.sort) return rows;
    const factor = sortState.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      switch (sortState.sort) {
        case 'subject':
          return a.subject.localeCompare(b.subject) * factor;
        case 'status':
          return a.status.localeCompare(b.status) * factor;
        case 'sent_at':
          return (
            ((a.sent_at ? Date.parse(a.sent_at) : 0) -
              (b.sent_at ? Date.parse(b.sent_at) : 0)) *
            factor
          );
        case 'recipients':
          return (a.sent_count - b.sent_count) * factor;
        default:
          return 0;
      }
    });
  }, [rows, sortState.sort, sortState.dir]);

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-border bg-background shadow-sm"
      style={{ clipPath: 'inset(0 round var(--radius-2xl))' }}
    >
      <div ref={scrollRef} className="overflow-x-auto">
        <div className="min-w-fit divide-y divide-border">
          <div
            className="hidden bg-muted/40 py-3 sm:grid"
            style={{ gridTemplateColumns: template }}
            role="row"
          >
            <span className="px-3" role="columnheader">
              <SortableHeaderButton
                column="subject"
                label={t('broadcast.list.colSubject', { defaultValue: 'Subject' })}
                state={sortState}
                className="font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
              />
            </span>
            <span className="px-2" role="columnheader">
              <SortableHeaderButton
                column="status"
                label={t('broadcast.list.colStatus', { defaultValue: 'Status' })}
                state={sortState}
                className="font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
              />
            </span>
            <span className="px-2" role="columnheader">
              <SortableHeaderButton
                column="sent_at"
                label={t('broadcast.list.colSentAt', { defaultValue: 'Sent at' })}
                state={sortState}
                className="font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
              />
            </span>
            <span className="px-2" role="columnheader">
              <SortableHeaderButton
                column="recipients"
                label={t('broadcast.list.colRecipients', { defaultValue: 'Recipients' })}
                state={sortState}
                className="font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
              />
            </span>
            <StickyEndCell elevated={false} bgClass="bg-muted/40" className="justify-end">
              <span className="sr-only">{t('rowActions.columnHeader')}</span>
            </StickyEndCell>
          </div>

          {sorted.map((row) => (
            <BroadcastRow
              key={row.id}
              row={row}
              siteBase={siteBase}
              template={template}
              elevated={elevated}
              dateFmt={dateFmt}
              onEdit={() => onEdit(row.id)}
              onDelete={() => onDelete(row.id, row.subject)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function BroadcastRow({
  row,
  siteBase,
  template,
  elevated,
  dateFmt,
  onEdit,
  onDelete,
}: {
  row: Broadcast;
  siteBase: string;
  template: string;
  elevated: boolean;
  dateFmt: Intl.DateTimeFormat;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation('common');
  const isSent = row.status === 'sent';
  const sentDate = row.sent_at ? dateFmt.format(new Date(row.sent_at)) : '—';
  // Recipient summary, shared between the mobile meta line and the desktop
  // column: count plus a parenthetical failed-count when any bounced.
  const recipientsText = isSent
    ? `${row.sent_count}${
        row.failed_count > 0
          ? ` (${row.failed_count} ${t('broadcast.list.failedSuffix', { defaultValue: 'failed' })})`
          : ''
      }`
    : '—';

  const menuActions: RowAction[] = [
    {
      id: 'edit',
      label: isSent
        ? t('broadcast.list.open', { defaultValue: 'Open' })
        : t('broadcast.list.edit', { defaultValue: 'Edit' }),
      icon: PencilSimple,
      onSelect: onEdit,
    },
    // Only offered once we know where the public site actually lives —
    // siteBase is the published website's own origin, so this opens the real
    // article instead of a path on the admin origin that serves nothing.
    ...(isSent && row.publish_to_website && row.slug && siteBase
      ? [
          {
            id: 'visit',
            label: t('broadcast.list.viewOnSite', { defaultValue: 'View on site' }),
            icon: ArrowSquareOut,
            onSelect: () =>
              window.open(
                `${siteBase}/news/${encodeURIComponent(row.slug)}`,
                '_blank',
                'noopener',
              ),
          } satisfies RowAction,
        ]
      : []),
    {
      id: 'delete',
      label: t('broadcast.list.delete', { defaultValue: 'Delete' }),
      icon: Trash,
      onSelect: onDelete,
      destructive: true,
    },
  ];

  // Translation-coverage pills are hidden along with the editor's translation
  // panel (see BroadcastEditor). With no way to author a translation, the row
  // would always collapse to a single pill repeating the org's own language on
  // every website-published article — noise with nothing actionable behind it.
  // Restore this block when per-recipient localization ships.

  const badges = (
    <>
      {row.imported_from_seed && (
        <span className="rounded-pill bg-muted px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider text-muted-foreground">
          {t('broadcast.list.importedBadge', { defaultValue: 'Pre-imported' })}
        </span>
      )}
      {row.publish_to_website && (
        <span className="rounded-pill bg-[var(--lime-light,#D4F56A)] px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider text-foreground">
          {t('broadcast.list.websiteBadge', { defaultValue: 'On website' })}
        </span>
      )}
    </>
  );

  return (
    <div
      className="group grid items-stretch transition-colors hover:bg-muted/40"
      style={{ gridTemplateColumns: template }}
      role="row"
    >
      {/* Mobile: everything stacks into the first column, spanning up to
          the sticky actions cell. The grid columns collapse to a single
          readable card per row instead of squeezing four columns into a
          phone viewport. Hidden at sm+. */}
      <div
        className="flex min-w-0 flex-col gap-1.5 px-3 py-3 sm:hidden"
        style={{ gridColumn: '1 / -2' }}
      >
        <button
          type="button"
          className="text-left font-heading text-sm font-semibold text-foreground hover:underline"
          onClick={onEdit}
        >
          {row.subject}
        </button>
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusBadge status={row.status} scheduledAt={row.scheduled_at} />
          {badges}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          {isSent && (
            <span className="tabular-nums">
              {t('broadcast.list.colSentAt', { defaultValue: 'Sent at' })}: {sentDate}
            </span>
          )}
          <span className="tabular-nums">
            {t('broadcast.list.colRecipients', { defaultValue: 'Recipients' })}:{' '}
            {recipientsText}
          </span>
        </div>
      </div>

      {/* Desktop: one cell per column, sm+ only. */}
      <span
        className="hidden min-w-0 flex-col justify-center gap-0.5 px-3 py-3 sm:flex"
        role="cell"
      >
        <button
          type="button"
          className="text-left font-heading text-sm font-semibold text-foreground hover:underline"
          onClick={onEdit}
        >
          {row.subject}
        </button>
        {(row.imported_from_seed || row.publish_to_website) && (
          <span className="flex flex-wrap items-center gap-1.5">{badges}</span>
        )}
      </span>

      <span className="hidden items-center px-2 py-3 sm:flex" role="cell">
        <StatusBadge status={row.status} scheduledAt={row.scheduled_at} />
      </span>

      <span
        className="hidden items-center px-2 py-3 text-sm tabular-nums text-foreground/80 sm:flex"
        role="cell"
      >
        {sentDate}
      </span>

      <span
        className="hidden items-center px-2 py-3 text-sm tabular-nums text-foreground/80 sm:flex"
        role="cell"
      >
        {recipientsText}
      </span>

      <StickyEndCell elevated={elevated} bgClass="bg-background group-hover:bg-muted">
        <RowActions menuActions={menuActions} />
      </StickyEndCell>
    </div>
  );
}

function StatusBadge({
  status,
  scheduledAt,
}: {
  status: Broadcast['status'];
  scheduledAt?: string | null | undefined;
}) {
  const { t } = useTranslation('common');
  const isSent = status === 'sent';
  // A draft carrying a future scheduled_at reads as "scheduled", not a plain
  // draft, so the admin can tell at a glance it will send on its own.
  const isScheduled = status === 'draft' && !!scheduledAt;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-0.5 text-xs font-semibold',
        isSent
          ? 'bg-[var(--lime-light,#D4F56A)] text-foreground'
          : isScheduled
            ? 'bg-[var(--pink,#FFA6EA)]/40 text-foreground'
            : 'bg-muted text-foreground/70',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          isSent ? 'bg-[var(--lime,#BEE034)]' : isScheduled ? 'bg-[var(--pink,#FFA6EA)]' : 'bg-foreground/40',
        )}
      />
      {isScheduled
        ? t('broadcast.list.status.scheduled', { defaultValue: 'Scheduled' })
        : t(`broadcast.list.status.${status}`, { defaultValue: status })}
    </span>
  );
}
